import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Typography } from 'antd';
import { API } from '@/services/api.service';
import { getTeamContactsMailing } from '@/services/teamAssignments.service';

const { Text } = Typography;

const lower = (email) => String(email || '').trim().toLowerCase();

/**
 * The mails a publication will send, as the server groups them: recipients
 * outside the platform who get the same files share one mail, and everyone in
 * copy (the chosen HLB copies plus the company's bookkeeper) is on each mail,
 * except where they are already a recipient of it. Files the month cannot
 * build are left out by the server, so the grouping can only get coarser. Once
 * the client's confirmed contacts stand in for the pack's list, this shows them.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Array} props.recipients - [{ email, name, files }]
 * @param {Array} props.copies - HLB addresses in cc
 * @param {boolean} props.notifyClientUsers - Whether the client's users are emailed
 * @param {Array} props.fileOptions - [{ value, label }] pack file titles, in pack order
 * @returns {JSX.Element} The preview
 */
export default function MailPreview({
  companyId,
  recipients: packRecipients,
  copies: packCopies,
  notifyClientUsers,
  fileOptions,
}) {
  const { t } = useTranslation('clientReports');
  // Same query as the note above the recipients: one request.
  const { data: mailing } = useQuery({
    queryKey: ['team-contacts-mailing', companyId],
    queryFn: () => getTeamContactsMailing(companyId),
    enabled: !!companyId,
    retry: false,
    staleTime: 60 * 1000,
  });
  const fromContacts = mailing?.sections?.find((s) => s.section === 'REPORTS' && s.source === 'CONTACTS');
  const recipients = fromContacts
    ? fromContacts.to.map((email) => ({
        email,
        // Someone already on the pack keeps the files chosen for them, as on the server.
        files: packRecipients.find((r) => lower(r.email) === email)?.files || [],
      }))
    : packRecipients;
  const copies = fromContacts ? [...packCopies, ...fromContacts.cc] : packCopies;
  const { data: bookkeepersData } = useQuery({
    queryKey: ['client-reports-bookkeepers', companyId],
    queryFn: () => API.clientReports.getReleaseBookkeepers(companyId),
    staleTime: 60 * 1000,
  });
  const bookkeepers = bookkeepersData?.data || [];

  const cc = [
    ...new Set(
      [...copies, ...bookkeepers.map((b) => b.email)].map(lower).filter(Boolean)
    ),
  ];
  const titles = fileOptions.map((o) => o.value);

  const groups = new Map();
  recipients.forEach((r) => {
    const email = lower(r.email);
    if (!email) return;
    const files = r.files?.length
      ? titles.filter((title) => r.files.includes(title))
      : titles;
    if (!files.length) return;
    const key = files.join('\n');
    if (!groups.has(key)) groups.set(key, { files, to: [] });
    const group = groups.get(key);
    if (!group.to.includes(email)) group.to.push(email);
  });
  const mails = [...groups.values()];
  const allTitles = titles.join('\n');

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong>{t('release.preview.title')}</Text>
      {mails.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.7 }}>{t('release.preview.none')}</div>
      )}
      {mails.map((mail, index) => {
        const mailCc = cc.filter((email) => !mail.to.includes(email));
        return (
          <div
            key={mail.files.join('|')}
            style={{
              fontSize: 12,
              marginTop: 6,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--ant-color-border, rgba(128,128,128,0.3))',
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {t('release.preview.mail', {
                n: index + 1,
                files:
                  mail.files.join('\n') === allTitles
                    ? t('release.preview.allFiles')
                    : mail.files.join(', '),
                interpolation: { escapeValue: false },
              })}
            </div>
            <div>
              <Text type="secondary">{t('release.preview.to')}: </Text>
              {mail.to.join(', ')}
            </div>
            <div>
              <Text type="secondary">{t('release.preview.cc')}: </Text>
              {mailCc.length ? mailCc.join(', ') : t('release.preview.noCc')}
            </div>
          </div>
        );
      })}
      {notifyClientUsers && (
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
          {t('release.preview.clientUsers')}
        </div>
      )}
    </div>
  );
}
